import Alert from '@mui/material/Alert';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import IconButton from '@mui/material/IconButton';
import Popper from '@mui/material/Popper';
import PropTypes from 'prop-types';
import React, {useEffect, useRef, useState} from 'react';
import Tooltip from '@mui/material/Tooltip';
import copy from 'copy-to-clipboard';
import {useTranslation} from 'react-i18next';

function Notification({
  customNotificationComponent = null,
  hasCopyError = false,
  onDismiss,
  title,
}) {
  const {t} = useTranslation();
  function handleClick(e) {
    e.stopPropagation();
    onDismiss();
  }

  return (
    customNotificationComponent || (
      <Alert
        action={
          <IconButton
            aria-label={t('copyButton.iconLabel')}
            color="inherit"
            onClick={handleClick}
            size="small"
          >
            <CloseIcon fontSize="inherit" />
          </IconButton>
        }
        severity={hasCopyError ? 'error' : 'success'}
      >
        {hasCopyError ? t('copyButton.error') : t('copyButton.copied', {title})}
      </Alert>
    )
  );
}

Notification.propTypes = {
  customNotificationComponent: PropTypes.elementType,
  hasCopyError: PropTypes.bool,
  onDismiss: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
};

export default function CopyButton({
  buttonStyleOverrides = null,
  customNotificationComponent = null,
  notificationLeaveDelay = 2500,
  notificationPlacement = 'top',
  onCopy = () => {},
  'data-qe': qe = null,
  showNotification = true,
  size = 'small',
  textToCopy,
  tooltipPlacement = 'bottom',
  title = 'text',
  isDisabled = false,
}) {
  const {t} = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);
  const [isCopyNotificationOpen, setIsCopyNotificationOpen] = useState(false);
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const [hasCopyError, setHasCopyError] = useState(false);
  const timer = useRef();

  function handleCopyButtonClick(event) {
    const isTextCopied = copy(textToCopy);

    clearTimeout(timer.current);
    setAnchorEl(event.currentTarget);
    setIsCopyNotificationOpen(true);
    setIsTooltipOpen(false);
    setHasCopyError(!isTextCopied);
    onCopy(isTextCopied, event);

    timer.current = setTimeout(() => {
      setAnchorEl(null);
      setIsCopyNotificationOpen(false);
      setHasCopyError(false);
    }, notificationLeaveDelay);
  }

  function handleTooltipOpen() {
    setIsTooltipOpen(!isCopyNotificationOpen);
  }

  function handleTooltipClose() {
    setIsTooltipOpen(false);
  }

  function handleDismiss() {
    clearTimeout(timer.current);
    setIsCopyNotificationOpen(false);
  }

  useEffect(() => clearTimeout(timer.current), []);

  return (
    <>
      <Tooltip
        onClose={handleTooltipClose}
        onOpen={handleTooltipOpen}
        open={isTooltipOpen}
        placement={tooltipPlacement}
        title={t('copyButton.tooltip', {title})}
      >
        <IconButton
          data-qe={qe}
          onClick={handleCopyButtonClick}
          size={size}
          sx={{borderRadius: 1, padding: 0.5, ...buttonStyleOverrides}}
          disabled={isDisabled}
        >
          <ContentCopyIcon fontSize="inherit" />
        </IconButton>
      </Tooltip>
      {showNotification && (
        <Popper
          anchorEl={anchorEl}
          disablePortal
          open={isCopyNotificationOpen}
          placement={notificationPlacement}
          sx={{zIndex: 'tooltip'}}
        >
          <Notification
            customNotificationComponent={customNotificationComponent}
            hasCopyError={hasCopyError}
            onDismiss={handleDismiss}
            title={title}
          />
        </Popper>
      )}
    </>
  );
}

CopyButton.propTypes = {
  buttonStyleOverrides: PropTypes.object,
  customNotificationComponent: PropTypes.elementType,
  notificationLeaveDelay: PropTypes.number,
  notificationPlacement: PropTypes.oneOf([
    'auto',
    'auto-end',
    'auto-start',
    'bottom',
    'bottom-end',
    'bottom-start',
    'left',
    'left-end',
    'left-start',
    'right',
    'right-end',
    'right-start',
    'top',
    'top-end',
    'top-start',
  ]),
  onCopy: PropTypes.func,
  'data-qe': PropTypes.string,
  showNotification: PropTypes.bool,
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  textToCopy: PropTypes.string.isRequired,
  title: PropTypes.string,
  tooltipPlacement: PropTypes.oneOf([
    'bottom',
    'bottom-end',
    'bottom-start',
    'left',
    'left-end',
    'left-start',
    'right',
    'right-end',
    'right-start',
    'top',
    'top-end',
    'top-start',
  ]),
  isDisabled: PropTypes.bool,
};
